import { useEffect } from 'react';
import {
  SHADOW_DOM_STYLE_LOAD_EVENT,
  useShadowRoot,
} from '@backstage/plugin-techdocs-react';

const STYLE_ELEMENT_ID = 'rdap-techdocs-style-overrides';

// mkdocs-material's own .md-tabs bar already has a full-bleed background,
// but its .md-grid child caps the tab list to the theme's default content
// width — this is what makes the tabs look inset instead of spanning the
// bar. Widening that grid (and bumping the tab link size) is the only
// reachable way to affect it, since it's generated static HTML rendered
// into a shadow root per docs build, not something this plugin's own CSS
// can normally reach.
const OVERRIDE_CSS = `
  .md-tabs .md-grid {
    max-width: none;
    padding-left: 24px;
    padding-right: 24px;
  }
  .md-tabs__link {
    font-size: 0.9rem;
  }
`;

function applyOverrides(root: ShadowRoot) {
  let styleEl = root.getElementById(STYLE_ELEMENT_ID) as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = STYLE_ELEMENT_ID;
    root.appendChild(styleEl);
  }
  styleEl.textContent = OVERRIDE_CSS;
}

/**
 * A TechDocs "Content" addon (renders nothing) that widens and enlarges the
 * generated docs' own mkdocs-material tab bar. Re-applies on every
 * SHADOW_DOM_STYLE_LOAD_EVENT since the shadow root's content — including
 * our injected <style> — is replaced wholesale on every docs page
 * navigation, not just on first mount.
 */
export const RdapTechDocsStyleOverrides = () => {
  const shadowRoot = useShadowRoot();

  useEffect(() => {
    if (!shadowRoot) {
      return undefined;
    }
    applyOverrides(shadowRoot);
    const handleStyleLoad = () => applyOverrides(shadowRoot);
    shadowRoot.addEventListener(SHADOW_DOM_STYLE_LOAD_EVENT, handleStyleLoad);
    return () =>
      shadowRoot.removeEventListener(SHADOW_DOM_STYLE_LOAD_EVENT, handleStyleLoad);
  }, [shadowRoot]);

  return null;
};
